<script setup lang="ts">
import {
  type DefaultInputProps,
  type IFileState,
  type IPixabayImage,
} from "@/interfaces";
import InputWrapper from "@/stories/form/InputWrapper/InputWrapper.vue";
import { nanoid } from "nanoid";
import { FileAcceptTypes, FileSizes } from "@/enums";
import { computed, ref, toRefs } from "vue";
import FlexRow from "@/stories/layout/FlexRow/FlexRow.vue";
import FileGallery from "@/stories/displays/FileGallery/FileGallery.vue";
import { useFieldValidationError } from "@/composables/useFieldValidationError";
import FileTrigger from "@/stories/form/FileTrigger/FileTrigger.vue";
import ImageTrigger from "@/stories/form/ImageTrigger/ImageTrigger.vue";
import HalfFormGroup from "@/stories/form/HalfFormGroup/HalfFormGroup.vue";
import PixabayDialog from "@/stories/dialogs/PixabayDialog.vue";
import ImageEditorDialog from "@/stories/dialogs/ImageEditorDialog.vue";
import LoadingDialog from "@/stories/dialogs/LoadingDialog.vue";
import { sentryException } from "@/sentry";
import { simpleError } from "@/stories/displays/SimpleError/SimpleError.vue";
import useFileUploads from "@/composables/useFileUploads";
import type { SVGName } from "@/SVGs";

const id = nanoid();

interface Props extends Omit<DefaultInputProps, "label"> {
  modelValue: IFileState[] | undefined;
  maxFile: number;
  label?: string;
  accept: FileAcceptTypes;
  maxSize?: FileSizes;
  pixabaySupport?: boolean;
  autoHideTrigger?: boolean;
  /**
   * You can enable this feature to replace the selected images completely.
   */
  autoReplace?: boolean;
  nameEditing?: boolean;
  favorite?: boolean;
  isHalfFormGroupActive?: boolean;
  showFavoriteIcon?: boolean;
  imageGalleryEnabled?: boolean;
  /**
   * You can hide the trigger whenever you want. You can use this feature to
   * allow users select only 1 image.
   */
  hideTrigger?: boolean;
  allowPasteImage?: boolean;
  showTitle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  maxSize: FileSizes.Max15MB,
  pixabaySupport: false,
  autoHideTrigger: false,
  autoReplace: false,
  nameEditing: false,
  favorite: false,
  variant: "file",
  isHalfFormGroupActive: true,
  showFavoriteIcon: true,
  imageGalleryEnabled: true,
  hideTrigger: false,
  allowPasteImage: false,
  showTitle: true,
});

const emit = defineEmits<{
  "update:modelValue": [IFileState[] | undefined];
}>();

const {
  accept,
  maxSize,
  maxFile,
  modelValue,
  errorMessageKey,
  autoHideTrigger,
  autoReplace,
  validation,
  name,
  favorite,
  hideTrigger,
  allowPasteImage,
} = toRefs(props);

const croppingImageSrc = ref<string>();
const cropppingFile = ref<IFileState>();
const loading = ref(false);

const handleOnUpdate = (files: IFileState[] | undefined) => {
  emit("update:modelValue", files);

  if (validation?.value?.errors) {
    delete validation.value.errors[name.value];
  }
};

const {
  selectionError,
  showPixabayDialog,
  onFileInputClick,
  onFileSelection,
  handleDelete,
  handlePixabaySelection,
} = useFileUploads({
  modelValue,
  accept: accept.value,
  maxSize: maxSize.value,
  maxFile: maxFile.value,
  autoReplace: autoReplace.value,
  favorite: favorite.value,
  allowPasteImage: allowPasteImage.value,
  onUpdate: handleOnUpdate,
});

const currentErrorMessage = computed(() => {
  return selectionError.value || errorMessageKey.value;
});

const handleFavorite = (file: IFileState) => {
  const newState = (modelValue.value || []).map((item) => {
    const hasBase64 = file.base64 && file.base64.trim().length > 0;
    const hasURL = file.url && file.url.trim().length > 0;

    if (
      (hasBase64 && item.base64 === file.base64) ||
      (hasURL && item.url === file.url)
    ) {
      item.isFavorite = true;
    } else {
      item.isFavorite = undefined;
    }
    return item;
  });
  emit("update:modelValue", newState);
};

const imageTriggedEnabled = computed(() => {
  return [FileAcceptTypes.ImageOrDocument, FileAcceptTypes.OnlyImage].includes(
    accept.value,
  );
});

const isTriggerEnable = computed(() => {
  if (hideTrigger.value) {
    return false;
  }

  if (autoHideTrigger.value) {
    const visibleFiles = (modelValue.value || []).filter(
      (item) => item.action !== "delete",
    );
    return visibleFiles.length < maxFile.value;
  }

  return true;
});

const DOCUMENT_ICONS: Record<FileAcceptTypes, SVGName> = {
  [FileAcceptTypes.OnlyPDF]: "far-file-pdf",
  [FileAcceptTypes.OnlyImage]: "far-image",
  [FileAcceptTypes.OnlyDocument]: "far-file-lines",
  [FileAcceptTypes.ImageOrDocument]: "far-file-lines",
};

const { hasError } = useFieldValidationError(name, validation);

const openPixabayDialog = () => {
  showPixabayDialog.value = true;
};

const currentFileCount = computed(() => {
  return (modelValue.value || []).filter((file) => file.action !== "delete")
    .length;
});

const imageUrlToBase64 = async (url: string): Promise<string | undefined> => {
  const response = await fetch(url, {
    credentials: "same-origin",
  });
  const blob = await response.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result?.toString());
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const handleEditClick = async (file: IFileState) => {
  const url = file.url || file.base64;
  if (!url) {
    return;
  }

  try {
    loading.value = true;
    cropppingFile.value = file;
    const base64 = await imageUrlToBase64(url);
    if (base64) {
      croppingImageSrc.value = base64;
    }
  } catch (error) {
    sentryException(error);
    simpleError("global.error_api");
  } finally {
    loading.value = false;
  }
};

const handleCropApply = (croppedBase64?: string) => {
  croppingImageSrc.value = undefined;

  if (!croppedBase64 || !cropppingFile.value) {
    return;
  }

  // We can update it directly because it's an object reference.
  cropppingFile.value.id = undefined; // Should be a new file
  cropppingFile.value.base64 = croppedBase64; // Cropped data
  cropppingFile.value.action = "upload"; // Should be uploaded
  // We should clear it incase of this is a pixabay image
  cropppingFile.value.url = undefined;
  // If user is cropped the image, it's not the deault image anymore
  cropppingFile.value.isDefault = false;
};

const maxPixabaySelectionCount = computed(() => {
  if (autoReplace.value) {
    return 1;
  }

  return maxFile.value - currentFileCount.value;
});
</script>

<template>
  <FlexRow>
    <InputWrapper
      :name="name"
      :forId="id"
      :label="label"
      :description="description"
      :isRequired="isRequired"
      :inputKey="inputKey"
      :tooltip="tooltip"
      :disabled="disabled"
    >
      <div class="flex flex-col gap-4">
        <FileGallery
          v-if="imageGalleryEnabled && modelValue && modelValue.length > 0"
          :files="modelValue"
          :nameEditing="nameEditing"
          :validation="validation"
          :name="name"
          :favorite="favorite"
          :showFavoriteIcon="showFavoriteIcon"
          @delete="handleDelete"
          @favorite="handleFavorite"
          @edit="handleEditClick"
        />

        <div v-if="isTriggerEnable || currentFileCount < maxFile">
          <div v-if="isTriggerEnable" class="mb-2">
            <HalfFormGroup
              v-if="imageTriggedEnabled"
              :isActive="isHalfFormGroupActive"
            >
              <ImageTrigger
                :name="name"
                :error="!!hasError"
                :validation="validation"
                :pixabaySupport="pixabaySupport"
                :showTitle="showTitle"
                @clickFromComputer="onFileInputClick"
                @dropped="onFileSelection"
                @clickFromCloud="openPixabayDialog"
              />
            </HalfFormGroup>
            <FileTrigger
              v-else
              :icon="DOCUMENT_ICONS[accept]"
              :disabled="disabled"
              :accept="accept"
              :maxSize="maxSize"
              :showTitle="showTitle"
              @click="onFileInputClick"
              @dropped="onFileSelection"
            />
          </div>

          <div v-if="currentErrorMessage" class="text-red-500">
            {{ $t(currentErrorMessage) }}
          </div>
        </div>
      </div>
    </InputWrapper>
  </FlexRow>

  <ImageEditorDialog
    v-if="croppingImageSrc"
    :src="croppingImageSrc"
    @close="croppingImageSrc = undefined"
    @apply="handleCropApply"
  />

  <PixabayDialog
    v-if="showPixabayDialog"
    :max="maxPixabaySelectionCount"
    @close="showPixabayDialog = false"
    @select="handlePixabaySelection"
  />

  <LoadingDialog v-if="loading" />
</template>
